import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";
import type { AccountInfo } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "@/authConfig";
import { rememberCourseAfterLogout } from "@/lib/courseLogin";

export const msalInstance = new PublicClientApplication(msalConfig);

let initializationPromise: Promise<void> | null = null;

export const initializeMsal = (): Promise<void> => {
  if (!initializationPromise) {
    initializationPromise = msalInstance.initialize();
  }
  return initializationPromise!;
};

export const signOut = async (): Promise<void> => {
  await initializeMsal();
  rememberCourseAfterLogout();
  await msalInstance.logoutRedirect({
    postLogoutRedirectUri: window.location.origin,
  });
};

// Deduplicates concurrent acquireTokenSilent calls: if a request is already in
// flight, all callers wait on the same promise instead of each spawning its own
// hidden iframe.
let pendingTokenRequest: Promise<string | null> | null = null;

// A stale Entra session can make the hidden-iframe renewal fail permanently
// (the authorize endpoint answers 400, MSAL times out waiting for the hash),
// while an interactive redirect through the same endpoint still succeeds.
// After consecutive timeouts we therefore escalate to acquireTokenRedirect
// instead of leaving every request hanging for the full iframe timeout.
const IFRAME_TIMEOUT_CODES = ["timed_out", "monitor_window_timeout"];
const ESCALATION_THRESHOLD = 2;
// Survives the redirect round-trip, so a still-broken session can't put the
// app into a redirect loop.
const ESCALATION_GUARD_KEY = "auth.interactiveEscalationAt";
const ESCALATION_GUARD_MS = 2 * 60 * 1000;

// A user seeing a frozen app will often reload rather than wait — a plain
// in-memory counter would be wiped by that reload and never reach the
// threshold above, leaving them stuck reloading into the same freeze
// indefinitely. Stored in sessionStorage instead, so the streak survives
// exactly the reload it's meant to protect against.
const IFRAME_TIMEOUT_STREAK_KEY = "auth.iframeTimeoutStreak";

const isIframeTimeout = (error: unknown): boolean =>
  error instanceof BrowserAuthError &&
  IFRAME_TIMEOUT_CODES.includes(error.errorCode);

const recentlyEscalated = (): boolean => {
  const escalatedAt = Number(sessionStorage.getItem(ESCALATION_GUARD_KEY));
  return escalatedAt > 0 && Date.now() - escalatedAt < ESCALATION_GUARD_MS;
};

const recordIframeTimeout = (): number => {
  const streak = Number(sessionStorage.getItem(IFRAME_TIMEOUT_STREAK_KEY)) + 1;
  sessionStorage.setItem(IFRAME_TIMEOUT_STREAK_KEY, String(streak));
  return streak;
};

const resetIframeTimeoutStreak = (): void => {
  sessionStorage.removeItem(IFRAME_TIMEOUT_STREAK_KEY);
};

const escalateToInteractiveAuth = async (
  account: AccountInfo,
  claims?: string
): Promise<void> => {
  sessionStorage.setItem(ESCALATION_GUARD_KEY, String(Date.now()));
  try {
    // Navigates away; navigateToLoginRequestUrl brings the user back here.
    // claims carries a Conditional Access challenge (e.g. step-up MFA) from
    // the silent request forward, per MSAL's claims-challenge guidance:
    // https://learn.microsoft.com/en-us/entra/identity-platform/msal-error-handling-js#errors-that-require-interaction
    await msalInstance.acquireTokenRedirect({
      ...loginRequest,
      account,
      ...(claims ? { claims } : {}),
    });
  } catch (error) {
    console.warn("Interactive token escalation failed", error);
  }
};

export const getAccessToken = async (): Promise<string | null> => {
  if (pendingTokenRequest) return pendingTokenRequest;

  pendingTokenRequest = (async () => {
    try {
      await initializeMsal();
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) return null;

      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      resetIframeTimeoutStreak();
      return response.idToken;
    } catch (error) {
      console.warn("Failed to acquire token silently", error);

      // MSAL's own guidance: when silent acquisition can't proceed (sign-in,
      // consent, or MFA needed), the remediation is an interactive call, not
      // a retry. Unlike a timeout this is a definitive signal, so escalate
      // on the first occurrence rather than waiting for a streak.
      if (error instanceof InteractionRequiredAuthError) {
        if (!recentlyEscalated()) {
          await escalateToInteractiveAuth(
            msalInstance.getAllAccounts()[0],
            error.claims || undefined
          );
        }
        return null;
      }

      if (isIframeTimeout(error)) {
        const streak = recordIframeTimeout();
        if (streak >= ESCALATION_THRESHOLD && !recentlyEscalated()) {
          await escalateToInteractiveAuth(msalInstance.getAllAccounts()[0]);
        }
      }
      return null;
    }
  })().finally(() => {
    pendingTokenRequest = null;
  });

  return pendingTokenRequest;
};
